import React from "react";
import styled from "styled-components";

import Avatar from "../avatar/Avatar";
import { type DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import MessageMetaColumn from "./MessageMetaColumn";
import type { DmMessage } from "../../app/services/dmMessages";

const AVATAR_SIZE = 32;

interface ContractMessageRowProps {
  message: DmMessage;
  isOurs: boolean;
  showAvatar: boolean;
  /** 既読フィルタ用の現在ユーザー ID（通常メッセージと同じ既読表示のため）。 */
  currentUserId?: number | string;
  menuItems?: DropdownMenuItem[];
  children: React.ReactNode;
}

// 通常メッセージ（MessageItem）と同じく行コンテナで hover 時に 3 点メニューを
// 出すため、`container-type` と `&:hover .message-actions` を備える。
const ContainerTheir = styled.div`
  padding: 0px 0px 8px 0px;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  position: relative;
  container-type: inline-size;

  &:hover .message-actions {
    visibility: visible;
    opacity: 1;
  }
`;

const ContainerOurs = styled.div`
  padding: 0px 0px 8px 0px;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  position: relative;
  container-type: inline-size;

  &:hover .message-actions {
    visibility: visible;
    opacity: 1;
  }
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
`;

const AvatarSpacer = styled.div`
  width: ${AVATAR_SIZE}px;
  flex-shrink: 0;
`;

// 通常メッセージ（MessageItem の `MessageTheir` / `MessageOurs`）と同じ吹き出し
// スタイル。契約書専用の青枠カードにはせず、通常メッセージの中に契約情報が
// 同梱されているように見せる方針に揃える。
const BubbleTheir = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  padding: 8px;
  border-radius: 6px;
  background: radial-gradient(50% 50% at 50% 50%, #fff 0%, #fcfcfc 100%);
  border: 1px solid var(--soremo-border);
  word-break: break-word;
  max-width: 75%;
`;

const BubbleOurs = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  padding: 8px;
  border-radius: 6px;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(252, 252, 252, 0.05) 0%,
    rgba(0, 154, 206, 0.05) 100%
  );
  border: 1px solid rgba(0, 154, 206, 0.1);
  color: black;
  word-break: break-word;
  max-width: 75%;
`;

/**
 * 契約関連アイテム（契約書・契約更新）を通常メッセージと同じ左右非対称の
 * 吹き出しレイアウトで並べる行コンテナ。
 *
 * 契約の締結も更新も「メッセージと同じ見た目のまま、要点と契約書 PDF がその場で
 * 読める」形に揃えるため、`ContractTimelineItem`（締結）と
 * `ContractAmendedTimelineItem`（更新）で共有する。
 */
const ContractMessageRow: React.FC<ContractMessageRowProps> = ({
  message,
  isOurs,
  showAvatar,
  currentUserId,
  menuItems,
  children,
}) => {
  if (isOurs) {
    return (
      <ContainerOurs>
        <MessageMetaColumn
          isOurs
          createdAt={message.created_at}
          receivers={message.receivers}
          currentUserId={currentUserId}
          menuItems={menuItems}
          menuPlacement="bottom-start"
        />
        <BubbleOurs>{children}</BubbleOurs>
      </ContainerOurs>
    );
  }

  return (
    <ContainerTheir>
      {showAvatar && message.author ? (
        <AvatarWrapper>
          <Avatar
            size={AVATAR_SIZE}
            src={message.author.avatar || undefined}
            tooltipText={message.author.display_name}
            tooltipPlacement="bottom"
            name={message.author.display_name}
          />
        </AvatarWrapper>
      ) : (
        <AvatarSpacer />
      )}
      <BubbleTheir>{children}</BubbleTheir>
      <MessageMetaColumn
        isOurs={false}
        createdAt={message.created_at}
        receivers={message.receivers}
        currentUserId={currentUserId}
        menuItems={menuItems}
      />
    </ContainerTheir>
  );
};

export default ContractMessageRow;
